import { FC, MouseEvent, useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AddCircleOutline, MoreVert } from "@mui/icons-material";
import Moment from "../../../common/Moment/Moment";
import { EC_MEMBER_TYPE, ROLES, RoutingList } from "../../../../constants";
import styles from "../../EcMembers/ECMembers.module.scss";
import TableFooter from "../../IRB/IRBSubmission/Table/TableFooter/TableFooter";
import { useNavigate } from "react-router-dom";
import {
  useGetAllMeetingsLazyQuery,
  useRemoveMeetingMutation,
} from "../../../../generated/graphql";
import useAppStore from "../../../../states/zustand/appStore";
import { downloadFile, getFileName } from "../../../../utils/helper";
import { toast } from "react-toastify";
import WithPermission from "../../../common/WithPermission/WithPermission";
import DeleteConfirmationModal from "../../../common/confirmationModal/DeleteConfirmationModal/SubmitConfirmation";

const MeetingList: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(2);
  const [getAllMeetings, { data, loading, error }] =
    useGetAllMeetingsLazyQuery();
  const [removeMeeting] = useRemoveMeetingMutation();
  const { setLoader, setIsSubmitModalVisible } = useAppStore();

  const openConfirmationModal = () => {
    setIsSubmitModalVisible(true);
  };

  useEffect(() => {
    const _getAllMeetings = async () => {
      await getAllMeetings({
        variables: {
          meetingType: '',
          meetingDate: '',
          page: page + 1,
          perPage: rowsPerPage,
        },
      });
    };
    _getAllMeetings();
  }, [page, rowsPerPage]);

  useEffect(() => {
    setLoader(loading);
  }, [loading]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const handleMoreOpen = (event: MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const navigateToCreateMeeting = () => {
    navigate(RoutingList.meeting.create.path);
  };

  const handleView = () => {
    navigate(RoutingList.meeting.details.path.replace(":id", selectedId));
  };

  const handleEdit = () => {
    navigate(RoutingList.meeting.edit.path.replace(":id", selectedId));
  };

  const handleDelete = async () => {
    try {
      let res = await removeMeeting({
        variables: {
          _id: selectedId,
        },
      });
      if (!res.data) throw null;
      getAllMeetings({
        variables: {
          meetingType: '',
          meetingDate: '',
          page: 1,
          perPage: rowsPerPage,
        },
      });
      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    } finally {
      handleMoreClose();
    }
  };
  return (
    <>
      <Container maxWidth={false}>
        <Card>
          <CardContent className="pd-x-0 pd-y-0">
            <Box className="d-flex align-items-center justify-content-between pd-x-32 pd-t-32 pd-b-20">
              <Typography variant="h2" component="h2">
                All Meeting
              </Typography>
              <WithPermission roles={[ROLES.ECS]}>
                <Button
                  size={"small"}
                  variant={"contained"}
                  color={"error"}
                  onClick={navigateToCreateMeeting}
                  type="button"
                >
                  <AddCircleOutline
                    className="mg-r-10"
                    sx={{ width: ".875rem", height: ".875rem" }}
                  />
                  New Meeting
                </Button>
              </WithPermission>
            </Box>
            <TableContainer className="custom-table">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="pd-l-32">Meeting ID</TableCell>
                    <TableCell>MIRB /EC Meeting Date</TableCell>
                    <TableCell>Meeting Type</TableCell>
                    {/* <TableCell>Last date of submission</TableCell> */}
                    <TableCell>Agenda</TableCell>
                    <TableCell className="pd-r-32"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.getAllMeetings?.meetings?.map((m) => (
                    <TableRow
                      key={m._id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell className="pd-l-32">
                        <Typography
                          variant={"subtitle1"}
                          className="font-weight-semi-bold"
                        >
                          {m.meetingId.toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Moment date={m.meetingDate} format={"ll"} />
                      </TableCell>
                      <TableCell>
                        {m.meetingType}
                      </TableCell>
                      <TableCell>
                        <Moment date={m.lastSubmissionDate} format={"ll"} />
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => downloadFile(m.agenda)}
                          variant="text"
                          color="error"
                          className={`pd-x-0`}
                          sx={{textTransform: 'initial'}}
                        >
                          <i className={`icon-download mg-r-10`}></i>
                          {getFileName(m.agenda)}
                        </Button>
                      </TableCell>
                      <TableCell className="pd-r-32">
                        <IconButton
                          onClick={(e) => handleMoreOpen(e, m._id)}
                          sx={{ p: 0 }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableFooter
              count={data?.getAllMeetings?.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
        <Menu
          className={styles.menu}
          id="more-menu"
          anchorEl={moreAnchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(moreAnchorEl)}
          onClose={handleMoreClose}
        >
          <MenuItem onClick={handleView}>
            <Typography textAlign="center">View</Typography>
          </MenuItem>
          <WithPermission roles={[ROLES.ECS]}>
            <>
              <MenuItem onClick={handleEdit}>
                <Typography textAlign="center">Edit</Typography>
              </MenuItem>
              <MenuItem onClick={openConfirmationModal}>
                <Typography textAlign="center">Delete</Typography>
              </MenuItem>
            </>
          </WithPermission>
        </Menu>
      <DeleteConfirmationModal callBack={() => handleDelete()} />
      </Container>
    </>
  );
};

export default MeetingList;
